import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Personal email domains to reject
const PERSONAL_EMAIL_DOMAINS = [
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com"
];

export default function AccessRequestModal({ isOpen, onClose }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchConfig();
      setSubmitted(false);
      setFormData({});
      setErrors({});
    }
  }, [isOpen]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/access-request/config`);
      setConfig(response.data);
      // Initialize form data with empty values
      const initialData = {};
      (response.data.fields || []).forEach(field => {
        initialData[field.name] = "";
      });
      setFormData(initialData);
    } catch (error) {
      console.error("Error fetching form config:", error);
      toast.error("Failed to load form");
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const domain = email.toLowerCase().split("@")[1];
    if (PERSONAL_EMAIL_DOMAINS.includes(domain)) {
      return "Please use your official company email. Personal emails are not accepted.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address.";
    }
    return null;
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\d\s\-+()]{10,}$/;
    if (!phoneRegex.test(phone)) {
      return "Please enter a valid contact number.";
    }
    return null;
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear error when user types
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {};
    (config.fields || []).forEach(field => {
      const value = formData[field.name] || "";
      
      if (field.required && !value.trim()) {
        newErrors[field.name] = `${field.label} is required`;
      } else if (field.type === "email" && value) {
        const emailError = validateEmail(value);
        if (emailError) newErrors[field.name] = emailError;
      } else if (field.type === "tel" && value) {
        const phoneError = validatePhone(value);
        if (phoneError) newErrors[field.name] = phoneError;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      // Prepare request data
      const requestData = {
        full_name: formData.full_name || "",
        company_name: formData.company_name || "",
        email: formData.email || "",
        contact_no: formData.contact_no || "",
        additional_fields: {}
      };

      // Add any additional fields
      Object.keys(formData).forEach(key => {
        if (!["full_name", "company_name", "email", "contact_no"].includes(key)) {
          requestData.additional_fields[key] = formData[key];
        }
      });

      await axios.post(`${API}/access-request/submit`, requestData);
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting request:", error);
      const errorMessage = error.response?.data?.detail || "Failed to submit request. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setFormData({});
    setErrors({});
    onClose();
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Success Screen
  if (submitted && config) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              {config.successHeading}
            </h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              {config.successMessage}
            </p>
            <Button 
              onClick={handleClose}
              style={{ 
                backgroundColor: config.buttonBgColor,
                color: config.buttonTextColor
              }}
              className="px-8"
            >
              {config.successButtonText}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Form Screen
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
            {config?.heading}
          </DialogTitle>
          {config?.subheading && (
            <p className="text-muted-foreground mt-2">{config.subheading}</p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {(config?.fields || [])
            .sort((a, b) => a.order - b.order)
            .map((field) => (
              <div key={field.id || field.name}>
                <Label htmlFor={field.name} className="text-sm font-medium">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                
                {field.type === "textarea" ? (
                  <Textarea
                    id={field.name}
                    value={formData[field.name] || ""}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    className={`mt-1 ${errors[field.name] ? 'border-red-500' : ''}`}
                    rows={3}
                  />
                ) : field.type === "select" && field.options?.length > 0 ? (
                  <Select
                    value={formData[field.name] || ""}
                    onValueChange={(value) => handleInputChange(field.name, value)}
                  >
                    <SelectTrigger className={`mt-1 ${errors[field.name] ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder={field.placeholder || "Select..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={field.name}
                    type={field.type}
                    value={formData[field.name] || ""}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    className={`mt-1 ${errors[field.name] ? 'border-red-500' : ''}`}
                  />
                )}
                
                {errors[field.name] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors[field.name]}
                  </p>
                )}
                
                {field.type === "email" && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Only official company emails are accepted
                  </p>
                )}
              </div>
            ))}

          <Button
            type="submit"
            className="w-full mt-6"
            disabled={submitting}
            style={{ 
              backgroundColor: config?.buttonBgColor || '#3a29a6',
              color: config?.buttonTextColor || '#ffffff'
            }}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              config?.buttonText || "Submit Request"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
